import { useEffect, useState, useCallback } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Lock, Gem, Loader2, CheckCircle2, ShieldCheck, RefreshCw, X, ExternalLink, Coins } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const artUrl = (p) => (!p ? null : p.startsWith("http") ? p : `${BACKEND}${p}`);

// ---------------------------- Player: Premium Shop ----------------------------
export const PremiumShop = ({ onBack, sessionParam, cancelled, onClearReturn }) => {
  const [items, setItems] = useState(null);
  const [confirm, setConfirm] = useState(null);   // item pending real-money confirmation
  const [coinConfirm, setCoinConfirm] = useState(null);  // item pending coin confirmation
  const [coins, setCoins] = useState(0);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);   // {order, polling}
  const [restoring, setRestoring] = useState(false);

  const load = useCallback(() => {
    gameApi.get("/game/premium/items").then((r) => { setItems(r.data.items); setCoins(r.data.coins || 0); }).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (cancelled) { toast("Checkout cancelled — you were not charged."); onClearReturn && onClearReturn(); }
  }, [cancelled, onClearReturn]);

  // Poll payment status when returning from Stripe with a session id
  useEffect(() => {
    if (!sessionParam) return;
    let tries = 0;
    let stop = false;
    setReceipt({ loading: true });
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await gameApi.get(`/game/premium/order/${sessionParam}`);
        if (data.payment_status === "paid" && data.entitlement_granted) {
          setReceipt({ loading: false, order: data });
          load();
          onClearReturn && onClearReturn();
          return;
        }
        if (data.status === "failed" || data.status === "cancelled") {
          setReceipt({ loading: false, error: "Payment did not complete. You were not charged." });
          onClearReturn && onClearReturn();
          return;
        }
      } catch (e) { /* keep polling */ }
      if (!stop && tries < 12) setTimeout(poll, 2000);
      else if (!stop) { setReceipt({ loading: false, pending: true }); onClearReturn && onClearReturn(); }
    };
    poll();
    return () => { stop = true; };
  }, [sessionParam, load, onClearReturn]);

  const startCheckout = async (item) => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/premium/checkout", { item_id: item.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;   // hosted Stripe Checkout
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setBusy(false); }
  };

  const purchaseCoins = async (item) => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/premium/purchase-coins", { item_id: item.id });
      setCoins(data.coins);
      setCoinConfirm(null);
      load();
      toast.success(`${item.name} unlocked!`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not complete coin purchase"); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    setRestoring(true);
    try {
      const { data } = await gameApi.post("/game/premium/restore");
      if (data.count > 0) { toast.success(`Restored ${data.count} purchase(s)!`); load(); }
      else toast("No missing purchases to restore.");
    } catch (e) { toast.error("Could not restore purchases"); }
    finally { setRestoring(false); }
  };

  return (
    <div className="pb-16" data-testid="premium-shop-view">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="premium-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
        <Gem className="text-fuchsia-500" size={20} /><h2 className="font-display text-xl font-bold text-primary">Premium Shop</h2>
        <button data-testid="premium-restore" onClick={restore} disabled={restoring}
          className="ml-auto flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-bold text-primary active:scale-95">
          {restoring ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Restore Purchases
        </button>
      </div>

      <div className="mx-4 mt-3 flex items-start gap-2 rounded-xl border border-fuchsia-300/40 bg-fuchsia-500/5 px-3 py-2 text-xs text-fuchsia-800">
        <ShieldCheck size={15} className="mt-0.5 shrink-0" />
        <span>Real-money purchases unlock <b>visual collections & extra horse varieties</b> — never competition, training, or leaderboard advantages. Prices shown before checkout. Secure payments by Stripe.</span>
      </div>

      {!items ? <p className="p-10 text-center text-muted-foreground" data-testid="premium-loading">Loading store…</p>
        : items.length === 0 ? <p className="p-10 text-center text-sm text-muted-foreground">No premium items available yet.</p>
        : (
          <div className="mt-3 space-y-4 px-4">
            {items.map((it) => (
              <div key={it.id} data-testid={`premium-item-${it.id}`} className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                <div className="relative grid aspect-[16/9] w-full place-items-center bg-gradient-to-br from-fuchsia-900 to-indigo-900">
                  {it.artwork ? <img src={artUrl(it.artwork)} alt={it.name} className="h-full w-full object-cover" />
                    : <span className="text-sm font-semibold text-white/70">Artwork coming soon</span>}
                  {!it.owned && <span className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-black/50 text-white"><Lock size={15} /></span>}
                  {it.owned && <span data-testid={`premium-owned-badge-${it.id}`} className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-xs font-bold text-white"><CheckCircle2 size={13} /> Unlocked</span>}
                </div>
                <div className="p-4">
                  <h3 className="font-display text-lg font-bold text-primary">{it.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{it.description}</p>
                  {it.coats && (
                    <p className="mt-2 text-xs font-semibold text-secondary">{it.coats.length} exclusive coat variants · unique body model · appears randomly in future selections</p>
                  )}
                  {it.owned ? (
                    <div className="mt-3"><span data-testid={`premium-owned-${it.id}`} className="inline-flex items-center gap-1 rounded-xl bg-emerald-100 px-4 py-2 text-sm font-bold text-emerald-700"><CheckCircle2 size={15} /> Owned ✓{it.owned_since ? ` · ${it.owned_since.slice(0, 10)}` : ""}</span></div>
                  ) : (
                    <div className="mt-3 space-y-2" data-testid={`premium-options-${it.id}`}>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Unlock Options</p>
                      <div className="flex flex-wrap items-center gap-2">
                        {it.checkout_enabled && (
                          <button data-testid={`premium-buy-${it.id}`} onClick={() => { setConfirm(it); setAgree(false); }}
                            className="rounded-xl bg-fuchsia-600 px-4 py-2 text-sm font-bold text-white active:scale-95" data-price={it.price_display}>{it.price_display}</button>
                        )}
                        {it.checkout_enabled && it.coin_checkout_enabled && <span className="text-xs font-bold text-muted-foreground">OR</span>}
                        {it.coin_checkout_enabled && (
                          <button data-testid={`premium-buy-coins-${it.id}`} disabled={coins < it.coin_price} onClick={() => setCoinConfirm(it)}
                            className="flex items-center gap-1 rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-white disabled:opacity-50 active:scale-95">
                            <Coins size={14} /> {it.coin_price.toLocaleString()} Coins
                          </button>
                        )}
                        {!it.checkout_enabled && !it.coin_checkout_enabled && (
                          <span data-testid={`premium-unavailable-${it.id}`} className="rounded-xl bg-muted px-4 py-2 text-sm font-bold text-muted-foreground">Coming soon</span>
                        )}
                      </div>
                      {it.coin_checkout_enabled && coins < it.coin_price && (
                        <p className="text-[11px] text-amber-700" data-testid={`premium-coins-short-${it.id}`}>You need {(it.coin_price - coins).toLocaleString()} more coins to unlock this with coins.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Real-money confirmation */}
      {confirm && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="premium-confirm-modal">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-primary">{confirm.name}</h3>
              <button data-testid="premium-confirm-close" onClick={() => setConfirm(null)} className="rounded-full p-1 text-muted-foreground hover:bg-muted"><X size={18} /></button>
            </div>
            <p className="text-2xl font-black text-fuchsia-600" data-testid="premium-confirm-price">{confirm.price_display}</p>
            <ul className="mt-3 space-y-1 text-sm text-foreground/80">
              <li>• Permanent account unlock (one-time)</li>
              {confirm.coats && <li>• {confirm.coats.length} exclusive Kiger coat variants + Kiger body model</li>}
              <li>• Kiger HMA fixed for every horse in the pack</li>
              <li>• Kiger horses appear <b>randomly</b> during future makeover selections</li>
              <li>• A Kiger is <b>not guaranteed</b> in your next selection</li>
              <li>• Does <b>not</b> improve competition scores or leaderboard results</li>
            </ul>
            <label className="mt-4 flex items-start gap-2 text-xs text-foreground/80">
              <input data-testid="premium-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" />
              <span>I understand this uses real money and does not guarantee a Kiger horse in my next makeover selection.</span>
            </label>
            <div className="mt-4 flex gap-2">
              <button data-testid="premium-cancel-btn" onClick={() => setConfirm(null)}
                className="h-11 flex-1 rounded-xl border-2 border-border font-bold text-primary active:scale-95">Cancel</button>
              <button data-testid="premium-continue-checkout" disabled={!agree || busy} onClick={() => startCheckout(confirm)}
                className="h-11 flex-1 rounded-xl bg-fuchsia-600 font-bold text-white disabled:opacity-50 active:scale-95">
                {busy ? "Redirecting…" : "Continue to Secure Checkout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Coin confirmation */}
      {coinConfirm && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="premium-coin-confirm-modal">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-primary">Unlock {coinConfirm.name}?</h3>
              <button data-testid="premium-coin-close" onClick={() => setCoinConfirm(null)} className="rounded-full p-1 text-muted-foreground hover:bg-muted"><X size={18} /></button>
            </div>
            <div className="space-y-1.5 rounded-xl border border-amber-300/50 bg-amber-500/5 p-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">This will cost</span><span className="font-bold text-amber-600" data-testid="premium-coin-cost">{coinConfirm.coin_price.toLocaleString()} Coins</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Current balance</span><span className="font-bold text-primary">{coins.toLocaleString()} Coins</span></div>
              <div className="flex justify-between border-t border-border pt-1.5"><span className="text-muted-foreground">Balance after</span><span className="font-bold text-primary" data-testid="premium-coin-after">{(coins - coinConfirm.coin_price).toLocaleString()} Coins</span></div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Permanent account unlock — the exact same horses & content as the real-money version. No competition advantage.</p>
            <div className="mt-4 flex gap-2">
              <button data-testid="premium-coin-cancel" onClick={() => setCoinConfirm(null)}
                className="h-11 flex-1 rounded-xl border-2 border-border font-bold text-primary active:scale-95">Cancel</button>
              <button data-testid="premium-coin-confirm" disabled={busy || coins < coinConfirm.coin_price} onClick={() => purchaseCoins(coinConfirm)}
                className="h-11 flex-1 rounded-xl bg-amber-500 font-bold text-white disabled:opacity-50 active:scale-95">{busy ? "…" : "Confirm Purchase"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt */}
      {receipt && (
        <div className="fixed inset-0 z-[86] flex items-center justify-center bg-black/60 p-4" data-testid="premium-receipt-modal">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 text-center shadow-xl">
            {receipt.loading ? (
              <><Loader2 className="mx-auto animate-spin text-fuchsia-500" size={32} /><p className="mt-3 text-sm text-muted-foreground">Confirming your payment…</p></>
            ) : receipt.error ? (
              <><X className="mx-auto text-destructive" size={32} /><p className="mt-3 font-bold text-primary">{receipt.error}</p>
                <button data-testid="premium-receipt-close" onClick={() => setReceipt(null)} className="mt-4 h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground">Back to Shop</button></>
            ) : receipt.pending ? (
              <><Loader2 className="mx-auto text-amber-500" size={32} /><p className="mt-3 font-bold text-primary">Payment pending</p>
                <p className="mt-1 text-sm text-muted-foreground">We'll unlock your pack as soon as the payment confirms. Try "Restore Purchases" in a moment.</p>
                <button data-testid="premium-receipt-close" onClick={() => setReceipt(null)} className="mt-4 h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground">Done</button></>
            ) : (
              <>
                <CheckCircle2 className="mx-auto text-emerald-500" size={40} />
                <h3 className="mt-2 font-display text-xl font-bold text-primary">Purchase complete!</h3>
                <p className="mt-1 text-sm text-muted-foreground">The {receipt.order.product_name} has been permanently unlocked for your account.</p>
                <div className="mt-3 space-y-1 rounded-xl border border-border bg-muted/40 p-3 text-left text-xs">
                  <p><b>Product:</b> {receipt.order.product_name}</p>
                  <p><b>Amount:</b> ${(receipt.order.amount_cents / 100).toFixed(2)} {receipt.order.currency.toUpperCase()}</p>
                  <p><b>Date:</b> {(receipt.order.purchase_date || "").slice(0, 19).replace("T", " ")}</p>
                  <p><b>Status:</b> {receipt.order.status} · Permanent unlock</p>
                  <p className="text-muted-foreground">Support: {receipt.order.support_email}</p>
                </div>
                <div className="mt-4 flex gap-2">
                  <button data-testid="premium-view-pack" onClick={() => setReceipt(null)} className="h-11 flex-1 rounded-xl bg-fuchsia-600 font-bold text-white">View Unlocked Pack</button>
                  <button data-testid="premium-receipt-restore" onClick={restore} className="h-11 flex-1 rounded-xl border border-border font-bold text-primary">Restore</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------- Jo Admin: Premium Shop Management ----------------------------
export const PremiumAdmin = () => {
  const [items, setItems] = useState(null);
  const [orders, setOrders] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [kset, setKset] = useState(null);
  const [edit, setEdit] = useState(null);   // item being edited
  const [busy, setBusy] = useState(false);

  const load = () => {
    gameApi.get("/game/admin/premium/items").then((r) => setItems(r.data.items)).catch(() => setItems([]));
    gameApi.get("/game/admin/premium/orders").then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
    gameApi.get("/game/admin/premium/revenue").then((r) => setRevenue(r.data)).catch(() => {});
    gameApi.get("/game/admin/premium/settings").then((r) => setKset(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: edit.name, description: edit.description,
        active: edit.active, purchasable: edit.purchasable,
        per_account_limit: Number(edit.per_account_limit) || 1,
      };
      if (edit.priceInput === "") payload.clear_price = true;
      else payload.price_cents = Math.round(parseFloat(edit.priceInput) * 100);
      payload.coin_purchasable = !!edit.coin_purchasable;
      if (edit.coinPriceInput === "") payload.clear_coin_price = true;
      else payload.coin_price = Math.max(0, Math.round(parseFloat(edit.coinPriceInput) || 0));
      const { data } = await gameApi.put(`/game/admin/premium/items/${edit.id}`, payload);
      toast.success("Item saved");
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const uploadArt = async (item, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await gameApi.post(`/game/admin/premium/items/${item.id}/artwork`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Artwork uploaded"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const repair = async (orderId) => {
    try { const { data } = await gameApi.post(`/game/admin/premium/orders/${orderId}/repair`); toast.success(`Repair: ${data.result}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Repair failed"); }
  };
  const refund = async (orderId) => {
    if (!window.confirm("Record a full refund and revoke future access? Issue the money refund in the Stripe Dashboard.")) return;
    try { await gameApi.post("/game/admin/premium/refund", { order_id: orderId }); toast.success("Refund recorded"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
  };

  const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  const saveKset = async () => {
    try {
      await gameApi.put("/game/admin/premium/settings", {
        kiger_appearance_rate: Math.max(0, Math.min(1, parseFloat(kset.kiger_appearance_rate) || 0)),
        kiger_max_per_selection: Math.max(0, parseInt(kset.kiger_max_per_selection) || 0),
        kiger_allow_dupe_coats: !!kset.kiger_allow_dupe_coats,
      });
      toast.success("Kiger selection settings saved");
    } catch (e) { toast.error("Could not save settings"); }
  };

  const saveSset = async () => {
    try {
      await gameApi.put("/game/admin/premium/settings", {
        south_steens_appearance_rate: Math.max(0, Math.min(1, parseFloat(kset.south_steens_appearance_rate) || 0)),
        south_steens_max_per_selection: Math.max(0, parseInt(kset.south_steens_max_per_selection) || 0),
        south_steens_allow_dupe_coats: !!kset.south_steens_allow_dupe_coats,
      });
      toast.success("South Steens selection settings saved");
    } catch (e) { toast.error("Could not save settings"); }
  };

  const saveWset = async () => {
    try {
      await gameApi.put("/game/admin/premium/settings", {
        warm_springs_appearance_rate: Math.max(0, Math.min(1, parseFloat(kset.warm_springs_appearance_rate) || 0)),
        warm_springs_max_per_selection: Math.max(0, parseInt(kset.warm_springs_max_per_selection) || 0),
        warm_springs_allow_dupe_coats: !!kset.warm_springs_allow_dupe_coats,
      });
      toast.success("Warm Springs selection settings saved");
    } catch (e) { toast.error("Could not save settings"); }
  };

  return (
    <div className="mt-3 space-y-4" data-testid="premium-admin">
      {revenue && (
        <div className="grid grid-cols-4 gap-2" data-testid="premium-revenue">
          {[["Gross", usd(revenue.gross_cents)], ["Refunds", usd(revenue.refund_cents)], ["Net", usd(revenue.net_cents)], ["Paid orders", revenue.paid_total]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-border bg-card p-2 text-center"><p className="text-sm font-extrabold text-primary">{v}</p><p className="text-[10px] text-muted-foreground">{l}</p></div>
          ))}
        </div>
      )}

      {kset && (
        <div className="rounded-xl border border-fuchsia-300/40 bg-fuchsia-500/5 p-3" data-testid="kiger-settings">
          <p className="mb-2 text-sm font-bold text-primary">Kiger Selection Settings</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-muted-foreground">Appearance chance (0–1)
              <input data-testid="kiger-rate" type="number" step="0.01" min="0" max="1" value={kset.kiger_appearance_rate}
                onChange={(e) => setKset({ ...kset, kiger_appearance_rate: e.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-border bg-background px-2 text-sm" /></label>
            <label className="text-xs text-muted-foreground">Max Kigers per selection
              <input data-testid="kiger-max" type="number" min="0" max="10" value={kset.kiger_max_per_selection}
                onChange={(e) => setKset({ ...kset, kiger_max_per_selection: e.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-border bg-background px-2 text-sm" /></label>
          </div>
          <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <input data-testid="kiger-dupe" type="checkbox" checked={!!kset.kiger_allow_dupe_coats}
              onChange={(e) => setKset({ ...kset, kiger_allow_dupe_coats: e.target.checked })} /> Allow duplicate Kiger coats in one selection
          </label>
          <button data-testid="kiger-settings-save" onClick={saveKset} className="mt-2 h-9 w-full rounded-lg bg-fuchsia-600 text-xs font-bold text-white">Save Kiger Settings</button>
        </div>
      )}

      {kset && (
        <div className="rounded-xl border border-amber-300/40 bg-amber-500/5 p-3" data-testid="south-steens-settings">
          <p className="mb-2 text-sm font-bold text-primary">South Steens Selection Settings</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-muted-foreground">Appearance chance (0–1)
              <input data-testid="south-steens-rate" type="number" step="0.01" min="0" max="1" value={kset.south_steens_appearance_rate ?? 0.12}
                onChange={(e) => setKset({ ...kset, south_steens_appearance_rate: e.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-border bg-background px-2 text-sm" /></label>
            <label className="text-xs text-muted-foreground">Max South Steens per selection
              <input data-testid="south-steens-max" type="number" min="0" max="10" value={kset.south_steens_max_per_selection ?? 2}
                onChange={(e) => setKset({ ...kset, south_steens_max_per_selection: e.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-border bg-background px-2 text-sm" /></label>
          </div>
          <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <input data-testid="south-steens-dupe" type="checkbox" checked={!!kset.south_steens_allow_dupe_coats}
              onChange={(e) => setKset({ ...kset, south_steens_allow_dupe_coats: e.target.checked })} /> Allow duplicate South Steens coats in one selection
          </label>
          <button data-testid="south-steens-settings-save" onClick={saveSset} className="mt-2 h-9 w-full rounded-lg bg-amber-600 text-xs font-bold text-white">Save South Steens Settings</button>
        </div>
      )}

      {kset && (
        <div className="rounded-xl border border-teal-300/40 bg-teal-500/5 p-3" data-testid="warm-springs-settings">
          <p className="mb-2 text-sm font-bold text-primary">Warm Springs Selection Settings</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-muted-foreground">Appearance chance (0–1)
              <input data-testid="warm-springs-rate" type="number" step="0.01" min="0" max="1" value={kset.warm_springs_appearance_rate ?? 0.12}
                onChange={(e) => setKset({ ...kset, warm_springs_appearance_rate: e.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-border bg-background px-2 text-sm" /></label>
            <label className="text-xs text-muted-foreground">Max Warm Springs per selection
              <input data-testid="warm-springs-max" type="number" min="0" max="10" value={kset.warm_springs_max_per_selection ?? 2}
                onChange={(e) => setKset({ ...kset, warm_springs_max_per_selection: e.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-border bg-background px-2 text-sm" /></label>
          </div>
          <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <input data-testid="warm-springs-dupe" type="checkbox" checked={!!kset.warm_springs_allow_dupe_coats}
              onChange={(e) => setKset({ ...kset, warm_springs_allow_dupe_coats: e.target.checked })} /> Allow duplicate Warm Springs coats in one selection
          </label>
          <button data-testid="warm-springs-settings-save" onClick={saveWset} className="mt-2 h-9 w-full rounded-lg bg-teal-600 text-xs font-bold text-white">Save Warm Springs Settings</button>
        </div>
      )}

      <div>
        <p className="mb-1 text-sm font-bold text-primary">Products</p>
        {!items ? <Loading /> : items.map((it) => (
          <div key={it.id} data-testid={`premium-admin-item-${it.id}`} className="mb-2 rounded-xl border border-border bg-card p-3">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-lg bg-muted">
                {it.artwork ? <img src={artUrl(it.artwork)} alt="" className="h-full w-full object-cover" /> : <Gem size={18} className="text-muted-foreground" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold text-primary">{it.name}</p>
                <p className="text-xs text-muted-foreground">{it.price_display} · {(it.coin_price || 0).toLocaleString()} coins{it.coin_purchasable ? "" : " (coins off)"} · {it.active ? "Active" : "Inactive"} · {it.purchasable ? "Purchasable" : "Not purchasable"} · {it.owned_count} owned · {it.counts?.purchases || 0} sold</p>
              </div>
              <button data-testid={`premium-admin-edit-${it.id}`} onClick={() => setEdit({ ...it, priceInput: it.price_cents ? (it.price_cents / 100).toFixed(2) : "", coinPriceInput: it.coin_price != null ? String(it.coin_price) : "" })}
                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground">Edit</button>
            </div>
          </div>
        ))}
      </div>

      <div>
        <p className="mb-1 text-sm font-bold text-primary">Recent Orders</p>
        {!orders ? <Loading /> : orders.length === 0 ? <p className="text-xs text-muted-foreground">No orders yet.</p> : (
          <div className="space-y-1.5">
            {orders.slice(0, 25).map((o) => (
              <div key={o.id} data-testid={`premium-order-${o.id}`} className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-primary">#{o.player_number} · {usd(o.amount_cents)}</span>
                  <span className={`rounded-full px-2 py-0.5 font-bold ${o.status === "completed" ? "bg-emerald-100 text-emerald-700" : o.status === "manual_review" ? "bg-amber-100 text-amber-700" : o.status.includes("refund") ? "bg-rose-100 text-rose-700" : "bg-muted text-muted-foreground"}`}>{o.status}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span>{o.environment}</span><span>·</span><span>granted: {o.entitlement_granted ? "yes" : "no"}</span>
                  {o.payment_status === "paid" && !o.entitlement_granted && <button data-testid={`premium-repair-${o.id}`} onClick={() => repair(o.id)} className="ml-auto rounded bg-secondary px-2 py-0.5 font-bold text-white">Repair</button>}
                  {o.payment_status === "paid" && o.status === "completed" && <button data-testid={`premium-refund-${o.id}`} onClick={() => refund(o.id)} className="ml-auto rounded border border-rose-400 px-2 py-0.5 font-bold text-rose-600">Refund</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {edit && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="premium-edit-modal">
          <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl border border-border bg-card p-5 shadow-xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-primary">Edit Product</h3>
              <button onClick={() => setEdit(null)} className="rounded-full p-1 text-muted-foreground hover:bg-muted"><X size={18} /></button>
            </div>
            <label className="text-xs font-bold text-primary">Name</label>
            <input data-testid="premium-edit-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="mb-2 mt-1 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" />
            <label className="text-xs font-bold text-primary">Description</label>
            <textarea data-testid="premium-edit-desc" rows={3} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="mb-2 mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            <label className="text-xs font-bold text-primary">Price (USD, blank = "Price coming soon")</label>
            <input data-testid="premium-edit-price" value={edit.priceInput} onChange={(e) => setEdit({ ...edit, priceInput: e.target.value })} placeholder="e.g. 14.99" className="mb-2 mt-1 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" />
            <label className="text-xs font-bold text-primary">Coin price (whole coins, blank = coins disabled)</label>
            <input data-testid="premium-edit-coin-price" type="number" min="0" value={edit.coinPriceInput} onChange={(e) => setEdit({ ...edit, coinPriceInput: e.target.value })} placeholder="e.g. 100000" className="mb-2 mt-1 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" />
            <label className="text-xs font-bold text-primary">Per-account limit</label>
            <input data-testid="premium-edit-limit" type="number" min={1} value={edit.per_account_limit} onChange={(e) => setEdit({ ...edit, per_account_limit: e.target.value })} className="mb-2 mt-1 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" />
            <div className="mb-3 flex gap-4">
              <label className="flex items-center gap-2 text-sm"><input data-testid="premium-edit-active" type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> Active</label>
              <label className="flex items-center gap-2 text-sm"><input data-testid="premium-edit-purchasable" type="checkbox" checked={edit.purchasable} onChange={(e) => setEdit({ ...edit, purchasable: e.target.checked })} /> Real money</label>
              <label className="flex items-center gap-2 text-sm"><input data-testid="premium-edit-coin-purchasable" type="checkbox" checked={!!edit.coin_purchasable} onChange={(e) => setEdit({ ...edit, coin_purchasable: e.target.checked })} /> Coins</label>
            </div>
            <label className="text-xs font-bold text-primary">Product artwork</label>
            <input data-testid="premium-edit-artwork" type="file" accept="image/png,image/webp,image/jpeg" onChange={(e) => e.target.files[0] && uploadArt(edit, e.target.files[0])} className="mb-3 mt-1 block w-full text-xs" />
            <button data-testid="premium-edit-save" disabled={busy} onClick={save} className="h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60">{busy ? "Saving…" : "Save Product"}</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------- Jo Admin: Payment Settings ----------------------------
export const PaymentSettings = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => gameApi.get("/game/admin/premium/settings").then((r) => setS(r.data)).catch(() => setS(null));
  useEffect(() => { load(); }, []);
  if (!s) return <Loading />;
  const isLive = s.effective_mode === "live";
  const toggleLive = async () => {
    if (!s.live_enabled) {
      if (!window.confirm("Enable LIVE Mode? Real customers will be charged REAL money. Only do this after a final test.")) return;
    }
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/admin/premium/live-mode", { enable: !s.live_enabled, confirm: true });
      setS(data); toast.success(data.effective_mode === "live" ? "Live Mode ON" : "Live Mode OFF (Test)");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change mode"); }
    finally { setBusy(false); }
  };
  const Row = ({ label, value, testid }) => (
    <div className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span><span data-testid={testid} className="font-bold text-primary">{value}</span>
    </div>
  );
  return (
    <div className="mt-3" data-testid="payment-settings">
      <div className={`mb-3 rounded-xl border-2 p-3 text-center ${isLive ? "border-rose-400 bg-rose-500/10" : "border-emerald-400 bg-emerald-500/10"}`}>
        <p data-testid="ps-effective-mode" className={`font-display text-lg font-black ${isLive ? "text-rose-600" : "text-emerald-600"}`}>
          {isLive ? "● LIVE MODE — real charges" : "● TEST MODE — no real charges"}
        </p>
      </div>
      <div className="rounded-xl border border-border bg-card p-4">
        <Row label="Live keys configured" value={s.live_ready ? "Yes" : "No — add STRIPE_LIVE_SECRET_KEY"} testid="ps-live-ready" />
        <Row label="Live webhook secret" value={s.live_webhook_ready ? "Configured" : "Not set (add after deploy)"} testid="ps-live-webhook" />
        <Row label="Live webhook URL" value={s.live_webhook_path} testid="ps-live-webhook-path" />
        <Row label="Test connection" value={s.stripe_connected ? "Connected (managed)" : "Not connected"} testid="ps-connected" />
        <Row label="Business" value={s.business_name} />
        <Row label="Currency" value={(s.currency || "usd").toUpperCase()} />
        <Row label="Support email" value={s.support_email} />
      </div>
      <button data-testid="ps-toggle-live" onClick={toggleLive} disabled={busy || (!s.live_enabled && (!s.live_ready || !s.live_webhook_ready))}
        className={`mt-3 h-11 w-full rounded-xl font-bold text-white disabled:opacity-50 ${s.live_enabled ? "bg-rose-600" : "bg-emerald-600"}`}>
        {busy ? "…" : s.live_enabled ? "Switch back to Test Mode" : "Enable Live Mode"}
      </button>
      {(!s.live_ready || !s.live_webhook_ready) && !s.live_enabled && (
        <p className="mt-1 text-center text-xs text-muted-foreground" data-testid="ps-live-blocked">Add your live key + webhook secret (after deploying) to enable Live Mode.</p>
      )}
      <a data-testid="ps-open-dashboard" href={s.dashboard_url} target="_blank" rel="noopener noreferrer"
        className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground">
        <ExternalLink size={15} /> Open Stripe Dashboard
      </a>
      <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
        <Coins size={13} className="mt-0.5 shrink-0" /> This game is not a replacement for Stripe's official payout, tax, or accounting tools. When Live Mode is on, Stripe handles tax calculation, collection & filing.
      </p>
    </div>
  );
};

const Loading = () => <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</p>;
